import type { FitBoundsOptions, LatLngBounds, Map } from 'leaflet';

/** Leaflet isValid() can be true while lat/lng are NaN on malformed GeoJSON bounds. */
export function isUsableLatLngBounds(bounds: LatLngBounds | null | undefined): boolean {
  if (!bounds || typeof bounds.isValid !== 'function' || !bounds.isValid()) return false;
  try {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    return [sw.lat, sw.lng, ne.lat, ne.lng].every((v) => Number.isFinite(v));
  } catch {
    return false;
  }
}

function mapContainerHasSize(map: Map): boolean {
  const container = map.getContainer?.();
  if (!container) return false;
  return container.offsetWidth >= 2 && container.offsetHeight >= 2;
}

/** True when Leaflet panes exist and the container is attached (avoids _leaflet_pos races). */
export function isMapReadyForLayout(map: Map | null | undefined): boolean {
  if (!map?.getContainer) return false;
  const internal = map as unknown as { _loaded?: boolean; _mapPane?: unknown };
  if (!internal._loaded || !internal._mapPane) return false;
  const container = map.getContainer();
  if (!container.isConnected) return false;
  return mapContainerHasSize(map);
}

/** invalidateSize only when the map is fully attached (avoids _leaflet_pos TypeError on unmount/hidden tabs). */
export function safeInvalidateSize(
  map: Map,
  after?: () => void,
  options?: boolean | { animate?: boolean; pan?: boolean; debounceMoveend?: boolean }
): void {
  const run = () => {
    if (!isMapReadyForLayout(map)) return;
    try {
      map.invalidateSize(options);
      after?.();
    } catch {
      // map tearing down or panes not initialized
    }
  };
  try {
    map.whenReady(() => requestAnimationFrame(run));
  } catch {
    // map may already be removed
  }
}

/** fitBounds only when bounds and map container are valid (avoids Invalid LatLng NaN errors). */
export function safeFitBounds(map: Map, bounds: LatLngBounds, options?: FitBoundsOptions): void {
  if (!isUsableLatLngBounds(bounds)) return;

  const run = () => {
    if (!isUsableLatLngBounds(bounds) || !mapContainerHasSize(map)) return;
    try {
      map.fitBounds(bounds, options);
    } catch {
      // ignore malformed bounds / teardown races
    }
  };

  try {
    map.whenReady(() => {
      requestAnimationFrame(run);
    });
  } catch {
    // map may be tearing down
  }
}
